import type { MetaReducer } from '@ngrx/store';
import { NGRX_STORAGE_KEYS } from '../../../../core/store/ngrx-persisted-state.keys';
import { createStorageSyncMetaReducer } from '../../../../core/store/ngrx-storage-sync.meta-reducer';
import { ApplicationsListFeatureState } from './applications-list.models';
import { applicationsListFeature, initialApplicationsListState } from './applications-list.reducer';
import { getPaginationPage } from './applications-list.cache';

function shouldClearFromStorage(state: ApplicationsListFeatureState): boolean {
  const filters = state.filters;
  const filtersDefault =
    filters.studentId === null &&
    (filters.studentLabel === null || filters.studentLabel === '') &&
    filters.companyId === null &&
    (filters.companyLabel === null || filters.companyLabel === '') &&
    (filters.jobRole === '' || filters.jobRole === undefined) &&
    filters.fromDate === null &&
    filters.toDate === null &&
    filters.status === true &&
    filters.applicationBy === null &&
    filters.isRelatable === null;
  const paginationDefault =
    state.pagination.firstRowOffset === 0 &&
    state.pagination.pageSize === initialApplicationsListState.pagination.pageSize &&
    getPaginationPage(state.pagination) === 1 &&
    state.pagination.totalRecords === 0;
  const sortDefault =
    state.sort.sortField === '' &&
    state.sort.sortOrder === 1 &&
    state.sort.previousSortField === '' &&
    state.sort.previousSortOrder === 0;
  const noGridData = state.gridData === null;
  return filtersDefault && paginationDefault && sortDefault && noGridData;
}

function parseState(raw: unknown): ApplicationsListFeatureState | null {
  if (!raw || typeof raw !== 'object') return null;
  const partial = raw as Partial<ApplicationsListFeatureState>;
  if (!partial.filters || !partial.pagination || !partial.sort) return null;

  const paginationPartial = partial.pagination as ApplicationsListFeatureState['pagination'];
  const rawGridData = partial.gridData ?? null;
  const legacyTotalRecords =
    rawGridData && typeof rawGridData === 'object' && 'totalRecords' in rawGridData
      ? Number((rawGridData as { totalRecords?: number }).totalRecords) || 0
      : null;
  const normalizedGridData =
    rawGridData && typeof rawGridData === 'object' && Array.isArray(rawGridData.applications)
      ? { applications: rawGridData.applications }
      : null;

  const parsedPagination: ApplicationsListFeatureState['pagination'] = {
    ...initialApplicationsListState.pagination,
    ...paginationPartial,
    page: paginationPartial.page ?? getPaginationPage({
      ...initialApplicationsListState.pagination,
      ...paginationPartial
    }),
    totalRecords: paginationPartial.totalRecords ?? legacyTotalRecords ?? 0
  };

  const filters = partial.filters as ApplicationsListFeatureState['filters'] & {
    applicationBy?: string | boolean | null;
  };
  const applicationBy =
    typeof filters.applicationBy === 'string' && filters.applicationBy.trim() !== ''
      ? filters.applicationBy
      : null;

  return {
    filters: {
      ...initialApplicationsListState.filters,
      ...filters,
      applicationBy
    },
    pagination: parsedPagination,
    sort: partial.sort,
    gridData: normalizedGridData
  };
}

export const applicationsListMetaReducers: MetaReducer[] = [
  createStorageSyncMetaReducer<ApplicationsListFeatureState>({
    featureKey: applicationsListFeature.name,
    storageKey: NGRX_STORAGE_KEYS.applicationsList,
    shouldClearFromStorage,
    serialize: (state) => state,
    deserialize: parseState
  })
];
